import React, { useState, useEffect, useMemo } from "react";
import { AdminSkill, SkillType } from "../../types/admin";
import { skillsApi } from "../../services/api";
import { useToast } from "./Toast";
import deviconMetadata from "devicon/devicon.json";
import fontAwesomeMetadata from "@fortawesome/fontawesome-free/metadata/icon-families.json";

const skillTypeLabels: Record<SkillType, string> = {
  [SkillType.FRONTEND]: "Front-End",
  [SkillType.BACKEND]: "Back-End",
  [SkillType.DEVOPS_TOOLS]: "DevOps & Tools",
};

const skillTypeBadgeClass: Record<SkillType, string> = {
  [SkillType.FRONTEND]: "admin-badge-frontend",
  [SkillType.BACKEND]: "admin-badge-backend",
  [SkillType.DEVOPS_TOOLS]: "admin-badge-devops",
};

const emptySkill: Omit<AdminSkill, "id"> = {
  name: "",
  iconClass: "",
  iconColor: "",
  type: SkillType.FRONTEND,
};

type IconOption = {
  label: string;
  iconClass: string;
  iconColor: string;
  searchText: string;
};

type DeviconEntry = {
  name: string;
  altnames?: string[];
  tags?: string[];
  color?: string;
  versions?: {
    font?: string[];
  };
  aliases?: Array<{
    alias: string;
  }>;
};

type FontAwesomeStyle = {
  family: string;
  style: string;
};

type FontAwesomeEntry = {
  label?: string;
  search?: {
    terms?: string[];
  };
  familyStylesByLicense?: {
    free?: FontAwesomeStyle[];
  };
};

const fontAwesomeStylePrefix: Record<string, string> = {
  brands: "fa-brands",
  regular: "fa-regular",
  solid: "fa-solid",
};

const contextFontAwesomeNames = new Set([
  "address-book",
  "align-left",
  "arrow-turn-down",
  "bars",
  "bold",
  "briefcase",
  "chart-column",
  "chart-line",
  "check-circle",
  "chevron-left",
  "chevron-right",
  "clock",
  "code",
  "database",
  "download",
  "envelope",
  "exclamation-circle",
  "eye",
  "file-import",
  "flag",
  "folder-open",
  "gears",
  "github",
  "globe",
  "info-circle",
  "instagram",
  "italic",
  "language",
  "link",
  "linkedin-in",
  "location-dot",
  "palette",
  "pen",
  "right-from-bracket",
  "right-to-bracket",
  "save",
  "server",
  "spinner",
  "trash",
  "user",
  "user-pen",
  "user-plus",
  "whatsapp",
  "x-twitter",
  "xmark",
]);

const techAndWebKeywords = [
  "tech",
  "technology",
  "web",
  "website",
  "site",
  "internet",
  "cloud",
  "server",
  "database",
  "api",
  "code",
  "dev",
  "developer",
  "development",
  "programming",
  "software",
  "frontend",
  "backend",
  "devops",
  "framework",
  "language",
  "repository",
  "version control",
  "hosting",
  "deploy",
  "security",
  "network",
  "browser",
  "mobile",
  "desktop",
  "social",
  "brand",
  "github",
  "gitlab",
  "bitbucket",
  "linkedin",
  "instagram",
  "twitter",
  "discord",
  "slack",
  "youtube",
  "node",
  "react",
  "vue",
  "angular",
  "docker",
  "kubernetes",
  "azure",
  "aws",
  "google",
  "microsoft",
  "linux",
  "windows",
  "apple",
  "android",
];

const isRelevantFontAwesomeIcon = (
  iconName: string,
  entry: FontAwesomeEntry,
  style: FontAwesomeStyle
): boolean => {
  if (contextFontAwesomeNames.has(iconName)) {
    return true;
  }

  const searchableText = [
    iconName,
    entry.label || "",
    style.style,
    ...(entry.search?.terms ?? []),
  ]
    .join(" ")
    .toLowerCase();

  if (style.style === "brands") {
    return techAndWebKeywords.some((keyword) => searchableText.includes(keyword));
  }

  return techAndWebKeywords.some((keyword) => searchableText.includes(keyword));
};

const toLabel = (value: string): string =>
  value
    .split("-")
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(" ");

const buildDeviconOptions = (): IconOption[] => {
  const entries = deviconMetadata as DeviconEntry[];
  const options: IconOption[] = [];

  entries.forEach((entry) => {
    const variants = entry.versions?.font ?? [];
    variants.forEach((variant) => {
      const iconClass = `devicon-${entry.name}-${variant}`;
      options.push({
        label: `Devicon ${toLabel(entry.name)} (${variant})`,
        iconClass,
        iconColor: entry.color || "#E37D4F",
        searchText: [
          entry.name,
          variant,
          ...(entry.altnames ?? []),
          ...(entry.tags ?? []),
        ]
          .join(" ")
          .toLowerCase(),
      });
    });

    (entry.aliases ?? []).forEach((aliasEntry) => {
      const iconClass = `devicon-${entry.name}-${aliasEntry.alias}`;
      options.push({
        label: `Devicon ${toLabel(entry.name)} (${aliasEntry.alias})`,
        iconClass,
        iconColor: entry.color || "#E37D4F",
        searchText: [entry.name, aliasEntry.alias, ...(entry.altnames ?? []), ...(entry.tags ?? [])]
          .join(" ")
          .toLowerCase(),
      });
    });
  });

  return options;
};

const buildFontAwesomeOptions = (): IconOption[] => {
  const entries = fontAwesomeMetadata as Record<string, FontAwesomeEntry>;
  const options: IconOption[] = [];

  Object.entries(entries).forEach(([iconName, entry]) => {
    const freeStyles = entry.familyStylesByLicense?.free ?? [];

    freeStyles
      .filter((style) => style.family === "classic" && Boolean(fontAwesomeStylePrefix[style.style]))
      .forEach((style) => {
        if (!isRelevantFontAwesomeIcon(iconName, entry, style)) {
          return;
        }

        const prefix = fontAwesomeStylePrefix[style.style];
        const iconClass = `${prefix} fa-${iconName}`;
        options.push({
          label: `Font Awesome ${entry.label || toLabel(iconName)} (${style.style})`,
          iconClass,
          iconColor: style.style === "brands" ? "#ffffff" : "#E37D4F",
          searchText: [iconName, entry.label || "", style.style, ...(entry.search?.terms ?? [])]
            .join(" ")
            .toLowerCase(),
        });
      });
  });

  return options;
};

const allCatalogIconOptions: IconOption[] = [
  ...buildDeviconOptions(),
  ...buildFontAwesomeOptions(),
];

const iconRendersInCurrentStyles = (iconClass: string): boolean => {
  const element = document.createElement("i");
  element.className = iconClass;
  element.style.position = "absolute";
  element.style.opacity = "0";
  element.style.pointerEvents = "none";
  element.style.left = "-9999px";
  element.style.top = "-9999px";
  document.body.appendChild(element);

  const before = window.getComputedStyle(element, "::before");
  const content = before.getPropertyValue("content");
  const normalized = content.replace(/['"]/g, "").trim();

  document.body.removeChild(element);

  return Boolean(normalized && normalized !== "none" && normalized !== "normal");
};

const SkillsPage: React.FC = () => {
  const { showToast } = useToast();
  const [skills, setSkills] = useState<AdminSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<Omit<AdminSkill, "id">>(emptySkill);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filterType, setFilterType] = useState<SkillType | "ALL">("ALL");
  const [iconSearch, setIconSearch] = useState("");
  const [validIconClasses, setValidIconClasses] = useState<Set<string> | null>(null);

  const iconOptions = useMemo(() => {
    const map = new Map<string, IconOption>();

    allCatalogIconOptions.forEach((option) => {
      map.set(option.iconClass, option);
    });

    skills.forEach((skill) => {
      if (!skill.iconClass) return;
      if (!map.has(skill.iconClass)) {
        map.set(skill.iconClass, {
          label: skill.name || skill.iconClass,
          iconClass: skill.iconClass,
          iconColor: skill.iconColor || "#E37D4F",
          searchText: `${skill.name || ""} ${skill.iconClass}`.toLowerCase(),
        });
      }
    });

    return Array.from(map.values());
  }, [skills]);

  useEffect(() => {
    let cancelled = false;

    const validateIcons = () => {
      const valid = new Set<string>();

      iconOptions.forEach((option) => {
        if (iconRendersInCurrentStyles(option.iconClass)) {
          valid.add(option.iconClass);
        }
      });

      if (!cancelled) {
        setValidIconClasses(valid);
      }
    };

    setValidIconClasses(null);

    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const timer = window.setTimeout(validateIcons, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [iconOptions]);

  const renderableIconOptions = useMemo(() => {
    if (!validIconClasses) {
      return [];
    }

    return iconOptions.filter((option) => validIconClasses.has(option.iconClass));
  }, [iconOptions, validIconClasses]);

  const filteredIconOptions = useMemo(() => {
    const term = iconSearch.trim().toLowerCase();
    if (!term) return renderableIconOptions;

    return renderableIconOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.iconClass.toLowerCase().includes(term) ||
        option.searchText.includes(term)
    );
  }, [renderableIconOptions, iconSearch]);

  useEffect(() => {
    loadSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const response = await skillsApi.getAll();
      if (response.success && response.data) {
        setSkills(response.data);
      } else {
        showToast(response.message || "Failed to load skills", "error");
      }
    } catch {
      showToast("Failed to load skills", "error");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptySkill);
    setIconSearch("");
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = (skill: AdminSkill) => {
    setEditingId(skill.id);
    setForm({
      name: skill.name,
      iconClass: skill.iconClass,
      iconColor: skill.iconColor,
      type: skill.type,
    });
    setIconSearch(skill.iconClass);
    setErrors({});
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Skill name is required";
    if (!form.iconClass.trim()) newErrors.iconClass = "Icon class is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (editingId) {
        const response = await skillsApi.update(editingId, form);
        if (response.success) {
          showToast(response.message || "Skill updated successfully");
          setModalOpen(false);
          loadSkills();
        } else {
          showToast(response.message || "Failed to update", "error");
        }
      } else {
        const response = await skillsApi.create(form);
        if (response.success) {
          showToast(response.message || "Skill created successfully");
          setModalOpen(false);
          loadSkills();
        } else {
          showToast(response.message || "Failed to create", "error");
        }
      }
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm("Are you sure you want to delete this skill?")) return;

    try {
      const response = await skillsApi.delete(id);
      if (response.success) {
        showToast(response.message || "Skill deleted");
        loadSkills();
      } else {
        showToast(response.message || "Failed to delete", "error");
      }
    } catch {
      showToast("An error occurred", "error");
    }
  };

  const filteredSkills =
    filterType === "ALL" ? skills : skills.filter((s) => s.type === filterType);

  const countByType = (type: SkillType) => skills.filter((s) => s.type === type).length;

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Skills</h1>
        <p className="admin-page-subtitle">
          Manage your technical skills — {skills.length} total
        </p>
      </div>

      {/* Stats Row */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" }}>
        <div className="admin-badge admin-badge-frontend" style={{ padding: "0.6rem 1.2rem", fontSize: "1.3rem" }}>
          Front-End: {countByType(SkillType.FRONTEND)}
        </div>
        <div className="admin-badge admin-badge-backend" style={{ padding: "0.6rem 1.2rem", fontSize: "1.3rem" }}>
          Back-End: {countByType(SkillType.BACKEND)}
        </div>
        <div className="admin-badge admin-badge-devops" style={{ padding: "0.6rem 1.2rem", fontSize: "1.3rem" }}>
          DevOps & Tools: {countByType(SkillType.DEVOPS_TOOLS)}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span className="admin-card-title">Skills</span>
            <select
              className="admin-form-select"
              style={{ width: "auto", padding: "0.5rem 2.5rem 0.5rem 1rem", fontSize: "1.2rem" }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as SkillType | "ALL")}
            >
              <option value="ALL">All Types</option>
              <option value={SkillType.FRONTEND}>Front-End</option>
              <option value={SkillType.BACKEND}>Back-End</option>
              <option value={SkillType.DEVOPS_TOOLS}>DevOps & Tools</option>
            </select>
          </div>
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={openCreate}>
            <i className="fa-solid fa-plus" />
            <span>Add Skill</span>
          </button>
        </div>

        {filteredSkills.length === 0 ? (
          <div className="admin-empty-state">
            <i className="fa-solid fa-code" />
            <p>No skills found</p>
            <button className="admin-btn admin-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" />
              <span>Add Your First Skill</span>
            </button>
          </div>
        ) : (
          <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSkills.map((skill) => (
                <tr key={skill.id}>
                  <td>
                    <i
                      className={skill.iconClass}
                      style={{ fontSize: "2rem", color: skill.iconColor }}
                    />
                  </td>
                  <td style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                    {skill.name}
                  </td>
                  <td>
                    <span className={`admin-badge ${skillTypeBadgeClass[skill.type]}`}>
                      {skillTypeLabels[skill.type]}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => openEdit(skill)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        onClick={() => handleDelete(skill.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="admin-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {editingId ? "Edit Skill" : "New Skill"}
              </h2>
              <button className="admin-modal-close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Skill Name</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.name ? " admin-input-error" : ""}`}
                  placeholder="e.g. React.js"
                  value={form.name}
                  onChange={(e) => { setForm((prev) => ({ ...prev, name: e.target.value })); setErrors((prev) => ({ ...prev, name: "" })); }}
                />
                {errors.name && <span className="admin-field-error">{errors.name}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Type</label>
                <select
                  className="admin-form-select"
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, type: e.target.value as SkillType }))
                  }
                >
                  <option value={SkillType.FRONTEND}>Front-End</option>
                  <option value={SkillType.BACKEND}>Back-End</option>
                  <option value={SkillType.DEVOPS_TOOLS}>DevOps & Tools</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Icon Class</label>
                <input
                  type="text"
                  className="admin-form-input"
                  placeholder="Search icon by name or class..."
                  value={iconSearch}
                  onChange={(e) => setIconSearch(e.target.value)}
                  style={{ marginBottom: "0.8rem" }}
                />
                <span className="admin-form-hint" style={{ display: "block", marginBottom: "0.8rem" }}>
                  {validIconClasses === null
                    ? "Validating available icons..."
                    : `${filteredIconOptions.length} icon${filteredIconOptions.length === 1 ? "" : "s"} found`}
                </span>
                <span className="admin-form-hint" style={{ display: "block", marginBottom: "0.8rem" }}>
                  Showing technology and website-related icons based on your portfolio context
                </span>
                <div
                  style={{
                    maxHeight: "18rem",
                    overflowY: "auto",
                    border: "1px solid var(--color-border)",
                    borderRadius: "0.8rem",
                    padding: "0.4rem",
                    marginBottom: "1rem",
                    background: "var(--color-surface-light)",
                  }}
                >
                  {filteredIconOptions.map((option) => (
                    <button
                      key={option.iconClass}
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          iconClass: option.iconClass,
                          iconColor: prev.iconColor || option.iconColor,
                        }))
                      }
                      style={{
                        width: "100%",
                        justifyContent: "flex-start",
                        marginBottom: "0.4rem",
                        borderColor:
                          form.iconClass === option.iconClass
                            ? "var(--color-secondary)"
                            : undefined,
                      }}
                    >
                      <i
                        className={option.iconClass}
                        style={{ color: option.iconColor, fontSize: "1.7rem" }}
                      />
                      <span>{option.label}</span>
                      <span style={{ marginLeft: "auto", opacity: 0.75 }}>
                        {option.iconClass}
                      </span>
                    </button>
                  ))}
                  {validIconClasses !== null && filteredIconOptions.length === 0 && (
                    <span className="admin-form-hint">No renderable icons found for this search.</span>
                  )}
                </div>
                <input
                  type="text"
                  className={`admin-form-input${errors.iconClass ? " admin-input-error" : ""}`}
                  placeholder="or type manually: e.g. devicon-react-original"
                  value={form.iconClass}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, iconClass: e.target.value }));
                    setErrors((prev) => ({ ...prev, iconClass: "" }));
                  }}
                />
                <span className="admin-form-hint">
                  Search and click an icon above, or type Devicon/Font Awesome class manually
                </span>
                {errors.iconClass && <span className="admin-field-error">{errors.iconClass}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Icon Color</label>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                  <input
                    type="color"
                    value={form.iconColor || "#E37D4F"}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, iconColor: e.target.value }))
                    }
                    style={{ width: "40px", height: "40px", border: "none", cursor: "pointer", background: "transparent" }}
                  />
                  <input
                    type="text"
                    className="admin-form-input"
                    placeholder="#61DAFB"
                    value={form.iconColor}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, iconColor: e.target.value }))
                    }
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Preview */}
              {form.iconClass && (
                <div className="admin-form-group">
                  <label className="admin-form-label">Preview</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      padding: "1.5rem",
                      background: "var(--color-surface-light)",
                      borderRadius: "0.8rem",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <i
                      className={form.iconClass}
                      style={{ fontSize: "3rem", color: form.iconColor }}
                    />
                    <span style={{ fontSize: "1.6rem", color: "var(--color-text-primary)" }}>
                      {form.name || "Skill Name"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
                <span>{saving ? "Saving..." : editingId ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsPage;
